import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import i18n from "@/i18n";
import {
  PREVIEW_ACK_TYPE,
  isPreviewInvalidateMessage,
  type PreviewAckMessage,
} from "@/lib/previewBridge";

/**
 * Mounted once at the app root (inside QueryClientProvider). When the public
 * site is loaded inside the admin editor's iframe, this component handles
 * partial-refresh requests posted by the editor — invalidating only the
 * affected React-Query keys instead of forcing a full document reload.
 *
 * Outside an iframe this is essentially a no-op: only messages whose `type`
 * matches PREVIEW_INVALIDATE_TYPE are handled, and other windows never send
 * them.
 */
const PreviewInvalidationListener = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const onMessage = async (e: MessageEvent) => {
      const data = e.data;
      if (!isPreviewInvalidateMessage(data)) return;

      const ack: PreviewAckMessage = { type: PREVIEW_ACK_TYPE, id: data.id, ok: true };
      try {
        if (data.lang && (data.lang === "tr" || data.lang === "en")) {
          if (i18n.language !== data.lang) {
            await i18n.changeLanguage(data.lang);
          }
        }
        if (Array.isArray(data.queryKeys)) {
          await Promise.all(
            data.queryKeys.map((key) =>
              queryClient.invalidateQueries({ queryKey: key as unknown[] }),
            ),
          );
        }
      } catch (err) {
        ack.ok = false;
        ack.error = err instanceof Error ? err.message : String(err);
      }
      try {
        // Reply directly to the origin window that sent the request.
        (e.source as Window | null)?.postMessage(ack, "*");
      } catch {
        /* source may have unmounted */
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [queryClient]);

  return null;
};

export default PreviewInvalidationListener;
